// AgreementGateDialog.tsx — модальный незакрываемый гейт по неподписанным редакциям
// пользовательского соглашения (план docs/plans/2026-07-31-agreement-versioning-v2.md, Этап 4, D23).
// Проверка идёт после MainStore.getCurrentUserInfo() в PrivateRoute; список берётся из
// GET Agreement/GetPending, подпись — тем же AgreementSignDialog, что и на регистрации.
import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import Dialog from "@mui/material/Dialog";
import MuiDialogContent from "@mui/material/DialogContent";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import MainStore from "./../MainStore";
import AuthStore from "features/Auth/store";
import Ckeditor from "components/ckeditor/ckeditor";
import CustomButton from "./Button";
import AgreementSignDialog from "./AgreementSignDialog";
import {
  ActiveAgreementVersion,
  PendingAgreement,
  getActiveAgreement,
  getPendingAgreements,
} from "api/Agreement";

interface AgreementGateDialogProps {
  /** Гейт проверяется только когда данные текущего пользователя уже загружены. */
  enabled: boolean;
}

const AgreementGateDialog = ({ enabled }: AgreementGateDialogProps) => {
  const { t, i18n } = useTranslation();

  const [pending, setPending] = useState<PendingAgreement | null>(null);
  const [version, setVersion] = useState<ActiveAgreementVersion | null>(null);
  const [signOpen, setSignOpen] = useState(false);

  const loadPending = useCallback(async () => {
    try {
      const response = await getPendingAgreements();
      const list: PendingAgreement[] = response?.data ?? [];
      const blocking = list.find((x) => x.blocks_login) ?? null;

      setPending(blocking);

      if (blocking) {
        const active = await getActiveAgreement(blocking.code);
        setVersion(active?.data ?? null);
      } else {
        setVersion(null);
      }
    } catch (err) {
      // Сбой проверки не должен запирать кабинет: гейт просто не показывается,
      // серверная проверка остаётся последней линией.
      setPending(null);
      setVersion(null);
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      loadPending();
    }
  }, [enabled, loadPending]);

  const handleSigned = async () => {
    setSignOpen(false);
    await loadPending();
  };

  if (!pending) {
    return null;
  }

  const content =
    i18n.language === "ru" ? version?.content_ru ?? "" : version?.content_kg ?? version?.content_ru ?? "";

  // Гейт срабатывает в двух ситуациях, и текст уведомления у них разный: пользователь либо
  // впервые подписывает соглашение, либо видит уже обновлённую редакцию. Отличаем по номеру:
  // редакция 1 — первая публикация, всё, что выше, — обновление.
  const isUpdatedVersion = pending.version_no > 1;
  const agreementName =
    (i18n.language === "ru" ? pending.name_ru : pending.name_kg ?? pending.name_ru) ?? "";

  return (
    <>
      <Dialog
        aria-labelledby="agreement-gate-dialog-title"
        fullWidth={true}
        id={"AgreementGateDialog"}
        maxWidth={"md"}
        open={!signOpen}
        disableEscapeKeyDown
      >
        <ContentWrapper>
          <Typography variant="h6" id="agreement-gate-dialog-title" gutterBottom>
            {isUpdatedVersion
              ? t("label:agreementGate.titleUpdated")
              : t("label:agreementGate.titleFirst")}
          </Typography>

          <Typography variant="body1" gutterBottom>
            {isUpdatedVersion
              ? t("label:agreementGate.noticeUpdated")
              : t("label:agreementGate.noticeFirst")}
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            {`${agreementName} · ${t("label:agreementGate.versionLabel", {
              version: pending.version_no,
            })}`}
          </Typography>

          <Paper
            variant="outlined"
            sx={{ maxHeight: 340, overflowY: "auto", padding: 2, backgroundColor: "rgba(0, 0, 0, 0.02)", mb: 2 }}
          >
            <Ckeditor
              onChange={() => {}}
              value={content}
              withoutPlaceholder
              disabled={true}
              name="agreementGateText"
              id="agreementGateText"
            />
          </Paper>

          <Typography variant="caption" color="text.secondary">
            {t("label:agreementGate.signHint")}
          </Typography>

          <ActionsWrapper>
            <CustomButton
              name="AgreementGateSign"
              id="AgreementGateSign"
              color={"primary"}
              variant="contained"
              onClick={() => setSignOpen(true)}
            >
              {t("label:digitalSign.sign")}
            </CustomButton>
            <CustomButton
              name="AgreementGateLogout"
              id="AgreementGateLogout"
              color={"error"}
              variant="contained"
              onClick={() => AuthStore.onLogout()}
            >
              {t("common:goOut")}
            </CustomButton>
          </ActionsWrapper>
        </ContentWrapper>
      </Dialog>

      <AgreementSignDialog
        open={signOpen}
        versionId={pending.version_id}
        defaultPin={MainStore.currentUserPin || ""}
        onSigned={handleSigned}
        onClose={() => setSignOpen(false)}
      />
    </>
  );
};

export default AgreementGateDialog;

const ContentWrapper = styled(MuiDialogContent)`
  margin: 20px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 24px;
`;
